import { useMemo } from 'react';

import { useSearchParams } from 'react-router';

import type { Session } from 'src/shared/session';

import { usePetitionDetailsPageData, usePetitionDetailsPageFcm } from 'src/app/pages/petitionGamma/details/pageContext';
import type { DecisionMaker } from 'src/app/pages/petitionGamma/details/shared/types';
import { useSessionAsync } from 'src/app/shared/hooks/session';
import { isLoaded } from 'src/app/shared/utils/async';

type Result = ModelHookResult<{
	showLegislativeBodies: boolean;
	trackingMetadata: { lbsPresent: boolean; fcEnabled: boolean; dataReadyForTracking: boolean };
}>;

export function useDecisionMakerSection(): Result {
	const { decisionMakersLegislativeBodiesEnabled: fcEnabled } = usePetitionDetailsPageFcm();
	const {
		data: {
			petition: {
				primaryDecisionMakerConnection: { nodes: allDms },
			},
		},
	} = usePetitionDetailsPageData();
	const session = useSessionAsync();

	const [params] = useSearchParams();
	const urlParamAllowedEntry = params.get('showLBs') === 'true';

	const loadedSession = isLoaded(session) ? session.value : undefined;

	const { lbsPresent, showLegislativeBodies } = useMemo(() => {
		if (!fcEnabled && !urlParamAllowedEntry) {
			return { lbsPresent: false, showLegislativeBodies: false };
		}
		return determineLbEligibility({
			allDms,
			loadedSession,
			urlParamAllowedEntry,
		});
	}, [fcEnabled, urlParamAllowedEntry, allDms, loadedSession]);

	const dataReadyForTracking = fcEnabled && lbsPresent && !!loadedSession;

	return {
		data: {
			showLegislativeBodies,
			trackingMetadata: { lbsPresent, fcEnabled, dataReadyForTracking },
		},
	};
}

const legislativeBodiesPresent = (dms: readonly DecisionMaker[]) => {
	// counts dms that can be grouped.
	// returns true if there's at least one group
	const lbNames = new Set();

	for (const dm of dms) {
		const { title, type } = dm;
		if (type === 'POLITICIAN' && title) {
			const lb = title.split('-')[0].trim();
			if (lbNames.has(lb)) return true;
			lbNames.add(lb);
		}
	}
	return false;
};

const determineLbEligibility = ({
	allDms,
	loadedSession,
	urlParamAllowedEntry,
}: {
	allDms: readonly DecisionMaker[];
	loadedSession: Session | undefined;
	urlParamAllowedEntry: boolean;
}) => {
	const lbsPresent = legislativeBodiesPresent(allDms);
	if (!lbsPresent) return { lbsPresent: false, showLegislativeBodies: false };

	if (urlParamAllowedEntry) return { lbsPresent: true, showLegislativeBodies: true };

	if (!loadedSession) {
		return { lbsPresent: true, showLegislativeBodies: false };
	}

	const userOrSessionId = loadedSession.user?.id ?? parseInt(loadedSession.uuid.at(-1) ?? '0', 16);
	const isUserOrSessionIdEven = Number(userOrSessionId) % 2 === 0;

	return { lbsPresent: true, showLegislativeBodies: isUserOrSessionIdEven };
};
